import { useQuery } from '@tanstack/react-query';
import { useCatalog } from './useCatalog';
import { supabase } from '@/lib/supabase';

interface OrderLineWithHeader {
  catalog_item_id: string;
  quantity: number;
  // PostgREST returns embedded relations as arrays even for many-to-one.
  // We expect 0 or 1 header per line.
  order: Array<{
    submitted_at: string | null;
    status: string;
  }> | null;
}

interface InvoiceLineRow {
  qbo_invoice_id: string;
  qbo_item_id: string | null;
  quantity: number | string | null;
}

export interface ItemFrequency {
  catalogItemId: string;
  totalQty: number;
  orderCount: number;
  lastOrderedAt: string | null;
}

interface OrderHistory {
  totalOrders: number;
  byCatalogItemId: Map<string, ItemFrequency>;
}

/**
 * useOrderHistory — aggregates this customer's order history into per-item
 * frequency stats. Powers the "Your regulars" section on /collection.
 *
 * Pulls from TWO sources and merges:
 *   1. orders.order_lines — orders placed in this portal.
 *   2. orders.v_invoice_lines — historical QBO invoices for this
 *      customer. Lets a brand-new portal user see their existing
 *      regulars on first sign-in instead of waiting until they place
 *      one order in here.
 *
 * Invoice lines are mapped to catalog rows via catalog.qbo_item_id
 * (numeric) ↔ v_invoice_lines.qbo_item_id (text). Lines without a
 * matching catalog row are skipped silently — credit memos, fees,
 * rentals, etc.
 *
 * RLS scopes to the active customer automatically.
 */
export function useOrderHistory() {
  const { data: catalog = [] } = useCatalog();

  return useQuery<OrderHistory>({
    queryKey: ['order-history', catalog.length],
    queryFn: async (): Promise<OrderHistory> => {
      const byItem = new Map<string, ItemFrequency>();
      const orderIds = new Set<string>();

      // ---- Source 1: in-app orders ----
      const orderLinesRes = await supabase
        .schema('orders')
        .from('order_lines')
        .select('catalog_item_id, quantity, order:orders(submitted_at, status)');

      if (!orderLinesRes.error) {
        const rows =
          (orderLinesRes.data as unknown as OrderLineWithHeader[] | null) ?? [];
        const skip = new Set(['draft', 'cancelled', 'rejected']);
        for (const row of rows) {
          const header = row.order?.[0] ?? null;
          const status = header?.status ?? 'submitted';
          if (skip.has(status)) continue;
          const id = row.catalog_item_id;
          const submittedAt = header?.submitted_at ?? null;

          const prev = byItem.get(id) ?? {
            catalogItemId: id,
            totalQty: 0,
            orderCount: 0,
            lastOrderedAt: null,
          };
          prev.totalQty += row.quantity;
          prev.orderCount += 1;
          if (
            submittedAt &&
            (!prev.lastOrderedAt || submittedAt > prev.lastOrderedAt)
          ) {
            prev.lastOrderedAt = submittedAt;
          }
          byItem.set(id, prev);

          if (submittedAt) orderIds.add(submittedAt);
        }
      }

      // ---- Source 2: QBO invoice history ----
      // Map catalog rows by qbo_item_id (as string) so we can match the
      // text column on v_invoice_lines.
      const catalogByQboItemId = new Map<string, string>();
      for (const c of catalog) {
        if (c.qboItemId != null) {
          catalogByQboItemId.set(String(c.qboItemId), c.id);
        }
      }
      if (catalogByQboItemId.size > 0) {
        const invoiceLinesRes = await supabase
          .schema('orders')
          .from('v_invoice_lines')
          .select('qbo_invoice_id, qbo_item_id, quantity');

        if (!invoiceLinesRes.error) {
          const rows = (invoiceLinesRes.data as InvoiceLineRow[] | null) ?? [];
          // Count distinct invoices per item: one per invoice id, not
          // one per line — credit memos etc. would otherwise double-count.
          const invoiceSeen = new Map<string, Set<string>>();
          for (const r of rows) {
            if (!r.qbo_item_id) continue;
            const catalogId = catalogByQboItemId.get(r.qbo_item_id);
            if (!catalogId) continue;
            const seen = invoiceSeen.get(catalogId) ?? new Set<string>();
            seen.add(r.qbo_invoice_id);
            invoiceSeen.set(catalogId, seen);

            const prev = byItem.get(catalogId) ?? {
              catalogItemId: catalogId,
              totalQty: 0,
              orderCount: 0,
              lastOrderedAt: null,
            };
            prev.totalQty += Number(r.quantity ?? 0);
            byItem.set(catalogId, prev);
            orderIds.add(r.qbo_invoice_id);
          }
          // Apply invoice-derived orderCount = number of distinct
          // invoices containing the item.
          for (const [catalogId, set] of invoiceSeen) {
            const prev = byItem.get(catalogId);
            if (!prev) continue;
            prev.orderCount = Math.max(prev.orderCount, set.size);
            byItem.set(catalogId, prev);
          }
        }
      }

      return { totalOrders: orderIds.size, byCatalogItemId: byItem };
    },
    staleTime: 60 * 1000,
    // Don't run until the catalog has resolved — otherwise the invoice
    // matcher would silently miss every line on first paint.
    enabled: catalog.length > 0,
  });
}
